import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { task, rawTimeout, timeout } from 'ember-concurrency';
import noUiSlider from 'nouislider';
import { add } from 'date-fns';
import { registerDestructor } from '@ember/destroyable';
import { isTesting, macroCondition } from '@embroider/macros';

export default class PositionControlComponent extends Component {
  @service stereo;
  @service feeds;
  @service intl;
  @service clock;

  // The pre-play range uses the airing's scheduled length; once the sound loads
  // we correct it to the sound's real duration exactly once.
  _rangeFromSound = false;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      // destroy() removes the slider's own event listeners, so there's no need
      // to off() them first.
      this.slider?.destroy();
    });
  }

  get showsSlider() {
    let feed = this.args.feed;
    if (!feed?.isLiveEdge) return true;
    return !!feed.isRewindable;
  }

  // The live-edge slider's second handle already marks the boundary.
  get showsAvailableExtent() {
    if (this.args.feed?.isLiveEdge) return false;
    return !!this.args.feed?.airing?.endsAt;
  }

  get availableUntil() {
    let endsAt = this.args.feed?.airing?.endsAt;
    if (!endsAt) return this.clock.date;
    return this.clock.date < new Date(endsAt) ? this.clock.date : new Date(endsAt);
  }

  get showsTrackHints() {
    return this.args.feed?.canScrub !== false && !!this.args.feed?.airing?.endsAt;
  }

  get regionItems() {
    let items = this.args.feed?.items ?? [];
    return items
      .filter((item) => item.status === 'published' && item.playedAt && item.stoppedAt)
      .sort((a, b) => a.playedAt - b.playedAt);
  }

  // While the show is on the air nothing past the playhead has been heard, so the hints stop there.
  get hintsWidth() {
    let feed = this.args.feed;
    if (!feed?.isLive || !this.airingDuration) return '100%';
    let position = feed.isLiveEdge ? feed.elapsedMs : feed.playheadPositionMs;
    return `${Math.min(100, Math.max(0, (position / this.airingDuration) * 100))}%`;
  }

  get startsAt() {
    return (
      this.args.feed?.airing?.actualStartsAt || this.args.feed?.airing?.startsAt
    );
  }

  // Both from the feed, never the sound: isRewindable is where the station's
  // say-so is read, and a relay advertising byte ranges makes the sound claim
  // to be seekable whether or not the station allows rewinding live.
  get isLiveAiring() {
    return !!(this.args.feed?.isLiveEdge && this.args.feed?.isRewindable);
  }

  get soundIsSeekable() {
    // A live broadcast is pinned to the live edge, never scrubbed in place.
    if (this.args.feed?.isLiveEdge) return false;
    return this.loadedSound?.isSeekable;
  }

  positionToTime = (position) => {
    if (this.startsAt && position) {
      return add(new Date(this.startsAt), {
        seconds: (position || 0) / 1000,
      });
    } else if (this.startsAt) {
      return this.startsAt;
    } else {
      return undefined;
    }
  };

  get airingDuration() {
    let endsAt = this.args.feed?.airing?.endsAt;
    if (!this.startsAt || !endsAt) return 0;
    return new Date(endsAt).getTime() - new Date(this.startsAt).getTime();
  }

  // noUiSlider requires finite numeric range bounds. A live stream's sound
  // reports an Infinite (or NaN) duration, which makes create()/updateOptions()
  // throw "'range' value isn't numeric". Coerce to a usable finite value, or
  // null when there's nothing valid to use.
  finiteMax(value) {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  get elapsedDuration() {
    if (!this.startsAt) return 0;
    return this.clock.date.getTime() - new Date(this.startsAt).getTime();
  }

  // Restartable so a feed switch (didInsert re-perform) cancels the in-flight
  // initialize instead of double-creating the slider, and re-performing cancels
  // the previous playhead loop rather than stacking another.
  initializeSliderTask = task({ restartable: true }, async () => {
    let min = 0;
    let max;
    this._rangeFromSound = false;

    if (this.isLiveAiring) {
      while (this.airingDuration <= 0) {
        await rawTimeout(100);
      }
      max = this.airingDuration;

      let elapsed = this.elapsedDuration;
      let startPosition = this.soundIsSeekable
        ? this.loadedSound.position
        : elapsed;

      this.slider = noUiSlider.create(this.sliderElement, {
        connect: [true, true, false],
        keyboardSupport: true,
        keyboardDefaultStep: 1000,
        tooltips: [
          {
            to: (value) =>
              this.intl.formatTime(this.positionToTime(value), {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: this.args.timeZone,
                locale: this.args.locale,
              }),
          },
          false,
        ],
        range: { min: [min, 1000], max: [max, 1000] },
        start: [startPosition, elapsed],
        margin: 0,
        cssPrefix: 'dubbletrack-slider-',
        handleAttributes: [
          {},
          { 'aria-hidden': 'true' },
        ],
      });

      let upperHandle = this.sliderElement.querySelector('[data-handle="1"]');
      if (upperHandle) {
        upperHandle.style.display = 'none';
        upperHandle.style.pointerEvents = 'none';
      }
    } else {
      // The airing window is the live-edge slider's range too, so a rewind keeps one scale. A live stream's Infinity is unusable.
      max =
        this.finiteMax(this.airingDuration) ??
        this.finiteMax(this.transportDuration);
      if (!max) {
        while (!this.finiteMax(this.transportDuration)) {
          await rawTimeout(10);
        }
        max = this.transportDuration;
      }

      this.slider = noUiSlider.create(this.sliderElement, {
        connect: [true, false],
        keyboardSupport: true,
        keyboardDefaultStep: 1000,
        tooltips: [
          {
            to: (value) =>
              this.intl.formatTime(this.positionToTime(value), {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZone: this.args.timeZone,
                locale: this.args.locale,
              }),
          },
        ],
        range: { min: [min, 1000], max: [max, 1000] },
        padding: [10],
        start: [this.args.feed?.playheadPositionMs ?? 0],
        cssPrefix: 'dubbletrack-slider-',
      });
    }

    // Respect the station's playback/scrubbing tolerance: when disabled, the
    // bar still tracks the playhead (setHandle works on a disabled slider) but
    // listeners can't drag or tap to seek.
    if (this.args.feed?.canScrub === false) {
      this.slider.disable();
    } else {
      this.slider.enable();
      this.slider.on('change', this.onSliderChange.bind(this));
      this.slider.on('slide', this.onSliderSlide.bind(this));
      this.slider.on('drag', this.onSliderSlide.bind(this));
    }
    this.updatePlayheadTask.perform();
  });

  goLive() {
    this.feeds.goLive();
  }

  onSliderSlide(values, handle) {
    if (this.isLiveAiring) {
      this.onSliderChange(values, handle);
      return;
    }

    let position = parseFloat(values[handle]);
    if (Number.isFinite(position)) {
      this.args.feed?.preview(position);
    }
    this.notifyUpdate(values, handle);
  }

  onSliderChange(values, handle) {
    if (this.isLiveAiring) {
      if (handle === 1) {
        this.slider.setHandle(1, this.elapsedDuration, false);
        return;
      }

      let rawPosition = parseFloat(values[0]);
      if (rawPosition >= this.elapsedDuration) {
        this.goLive();
        return;
      }

      this.slider.setHandle(0, rawPosition, false);

      if (this.soundIsSeekable) {
        this.loadedSound.position = rawPosition;
      } else {
        this.feeds.scrubToFeed(this.args.feed.airing, rawPosition);
      }
      return;
    }

    let position = parseFloat(values[handle]);

    if (position) {
      this.args.feed?.seekOrGoLive(position);
    }
    this.notifyUpdate(values, handle);
  }

  notifyUpdate(values, handle) {
    let payload = {
      handle: handle,
      time: this.positionToTime(values[handle]),
      position: values[handle],
    };

    if ('onUpdate' in this.args) {
      this.args.onUpdate(handle, payload);
    }
    if ('onUpdateStart' in this.args && handle == 0) {
      this.args.onUpdateStart(payload);
    }
    if ('onUpdateEnd' in this.args && handle == 1) {
      this.args.onUpdateEnd(payload);
    }
  }

  updatePlayheadTask = task({ restartable: true }, async () => {
    if (macroCondition(isTesting())) {
      return;
    }
    while (true) {
      if (this.slider) {
        if (this.isLiveAiring) {
          let elapsed = this.elapsedDuration;
          this.slider.setHandle(1, elapsed, false);

          // Don't fight a pending rewind: while the feed loads a new position,
          // leave the handle where the drag put it.
          if (!this.feeds.playFeedTask.isRunning) {
            this.slider.setHandle(0, elapsed, false);
          }
        } else if (this.transportIsPlaying) {
          // Adopting the sound's duration under an airing window would rescale the bar mid-rewind.
          let nextMax = null;
          if (!this.finiteMax(this.airingDuration) && !this._rangeFromSound) {
            nextMax = this.finiteMax(this.transportDuration);
            if (nextMax) this._rangeFromSound = true;
          }
          if (nextMax) {
            this.slider.updateOptions({
              range: {
                min: [0, 1000],
                max: [nextMax, 1000],
              },
            });
          }
          // A drag or seek in flight owns the handle until the transport catches up.
          if (
            this.args.feed?.pendingPositionMs == null &&
            Number.isFinite(this.transportPosition)
          ) {
            this.slider.setHandle(0, this.transportPosition, false);
          }
        }
      }
      await timeout(1000);
    }
  });

  get loadedSound() {
    return this.args.feed?.loadedSound;
  }

  // Transport contract — follows the remote device while casting (the local
  // sound is paused then), the local sound otherwise. See Feed#transport*.
  get transportPosition() {
    return this.args.feed?.transportPosition;
  }

  get transportDuration() {
    return this.args.feed?.transportDuration;
  }

  get transportIsPlaying() {
    return this.args.feed?.transportIsPlaying;
  }

  get transportIsSeekable() {
    return this.args.feed?.transportIsSeekable;
  }

  @action
  async didInsert(element) {
    this.sliderElement = element;
    if (this.slider) {
      this.slider.destroy();
    }
    this.initializeSliderTask.perform();
  }

  // Preview where a tap would seek to: track the cursor across the slider and
  // expose its position (0–100%) as a CSS var that lights up the bar up to it.
  @action
  onScrubHover(event) {
    // No seek preview when the station disallows scrubbing.
    if (this.args.feed?.canScrub === false) return;

    let element = event.currentTarget;
    let { left, width } = element.getBoundingClientRect();
    if (!width) return;

    let percent = Math.min(1, Math.max(0, (event.clientX - left) / width));
    element.style.setProperty('--scrub-hover', `${percent * 100}%`);
  }

  @action
  onScrubHoverLeave(event) {
    event.currentTarget.style.removeProperty('--scrub-hover');
  }
}
